import React from 'react';
import DatePicker from '../DatePicker';
import {
  isMeterTrigger,
  triggerLabel,
  triggerPlaceholder,
  triggerUnit,
  type TriggerOption,
} from '../../lib/maintenanceTriggers';
import type { MaintenanceSchedulePayload, MaintenanceTriggerType } from '../../types/equipment';

/* Same field styling as the Edit Equipment modal on the equipment detail page. */
const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

export interface ScheduleFormState {
  name: string;
  trigger_type: MaintenanceTriggerType | '';
  interval_value: string;
  last_performed_at: string;
  last_performed_value: string;
}

export const emptyScheduleForm = (trigger?: MaintenanceTriggerType): ScheduleFormState => ({
  name: '',
  trigger_type: trigger ?? '',
  interval_value: '',
  last_performed_at: '',
  last_performed_value: '',
});

/** Returns the first validation problem, or null when the form is submittable. */
export function scheduleFormError(form: ScheduleFormState): string | null {
  if (!form.name.trim()) return 'Enter a schedule name';
  if (!form.trigger_type) return 'Pick a trigger';
  const interval = Number(form.interval_value);
  if (!form.interval_value || !Number.isFinite(interval) || interval <= 0) {
    return 'Enter an interval greater than zero';
  }
  if (form.last_performed_value && !Number.isFinite(Number(form.last_performed_value))) {
    return 'Last performed reading must be a number';
  }
  return null;
}

export function scheduleFormPayload(form: ScheduleFormState): MaintenanceSchedulePayload {
  const meterBased = isMeterTrigger(form.trigger_type as MaintenanceTriggerType);
  return {
    name: form.name.trim(),
    trigger_type: form.trigger_type as MaintenanceTriggerType,
    interval_value: Number(form.interval_value),
    last_performed_at: form.last_performed_at || null,
    last_performed_value:
      meterBased && form.last_performed_value ? Number(form.last_performed_value) : null,
  };
}

interface ScheduleFormFieldsProps {
  form: ScheduleFormState;
  onChange: (patch: Partial<ScheduleFormState>) => void;
  /** Asset-aware trigger list from `triggerOptionsFor`. */
  options: TriggerOption[];
  /**
   * Set when editing a schedule whose trigger is not offered for this asset
   * kind (a curl-created `meters_run`, or an odometer unit that does not match
   * the company's unit system). The select locks so saving cannot silently
   * rewrite the trigger.
   */
  lockedTrigger?: MaintenanceTriggerType | null;
}

export function ScheduleFormFields({ form, onChange, options, lockedTrigger }: ScheduleFormFieldsProps) {
  const trigger = form.trigger_type as MaintenanceTriggerType;
  const meterBased = isMeterTrigger(trigger);
  const unit = triggerUnit(trigger);

  return (
    <>
      <div>
        <label className={labelClass}>Schedule name *</label>
        <input
          className={inputClass}
          placeholder="e.g. Oil &amp; filter change"
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Trigger *</label>
          {lockedTrigger ? (
            <>
              <select className={`${inputClass} opacity-60`} value={lockedTrigger} disabled>
                <option value={lockedTrigger}>{triggerLabel(lockedTrigger)}</option>
              </select>
              <p className="text-[11px] text-fieldiq-muted mt-1">
                {lockedTrigger === 'meters_run'
                  ? 'Not available for new schedules — footage attribution is undecided, so this schedule cannot be evaluated.'
                  : 'Not offered for this unit — kept as-is so saving does not change it.'}
              </p>
            </>
          ) : (
            <select
              className={inputClass}
              value={form.trigger_type}
              onChange={(e) => {
                const next = e.target.value as MaintenanceTriggerType;
                // Drop a stale meter reading when switching to a date/count trigger.
                onChange({
                  trigger_type: next,
                  last_performed_value: isMeterTrigger(next) ? form.last_performed_value : '',
                });
              }}
            >
              <option value="">Select trigger...</option>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className={labelClass}>Interval{unit ? ` (${unit})` : ''} *</label>
          <input
            className={inputClass}
            type="number"
            min={1}
            placeholder={triggerPlaceholder(trigger) || 'e.g. 90'}
            value={form.interval_value}
            onChange={(e) => onChange({ interval_value: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Last performed (optional)</label>
          <DatePicker
            value={form.last_performed_at}
            onChange={(v) => onChange({ last_performed_at: v })}
            placeholder="Never performed"
          />
        </div>
        {meterBased && (
          <div>
            <label className={labelClass}>Reading at last service ({unit})</label>
            <input
              className={inputClass}
              type="number"
              min={0}
              placeholder="Optional"
              value={form.last_performed_value}
              onChange={(e) => onChange({ last_performed_value: e.target.value })}
            />
          </div>
        )}
      </div>

      <p className="text-[11px] text-fieldiq-muted">
        {meterBased
          ? `Due state is computed from the unit's latest ${unit} reading. Until a reading exists the schedule shows as Unknown.`
          : 'Leave "last performed" empty to start the clock from the first time you mark it performed.'}
      </p>
    </>
  );
}
